import { Suspense } from "react";
import { StyleSheet, View } from "react-native";
import { ErrorPage } from "../pages/ErrorPage";
import { LoadingPage } from "../pages/LoadingPage";
import { ApplicationKey } from "../utils/applications";
import { colors, invariantColor } from "../utils/design";
import { t } from "../utils/i18n";
import { ErrorBoundary } from "./ErrorBoundary";
import { Icon } from "./Icon";
import { Space } from "./Space";
import { Link, Text } from "./Text";

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    width: "100%",
    height: "100%",
  },
  content: {
    flex: 1,
  },

  backLink: {
    display: "flex",
    alignItems: "center",
  },
});

type Props = {
  children: React.ReactNode;
  fromApp: ApplicationKey | undefined;
};

export const AppLayout: React.FC<Props> = ({ children }) => {
  return (
    <View style={styles.container}>
      <ErrorBoundary fallback={(reset, error) => <ErrorPage resetError={reset} error={error} />}>
        <Suspense fallback={<LoadingPage />}>
          <View style={styles.content}>{children}</View>
        </Suspense>
      </ErrorBoundary>
    </View>
  );
};

type PageTitleProps = {
  title: string;
  description?: React.ReactNode;
};

export const PageTitle: React.FC<PageTitleProps> = ({ title, description }) => {
  return (
    <>
      <Text variant="title2" color={invariantColor.white}>
        {title}
      </Text>

      {description != null && (
        <>
          <Space height={8} />
          <Text variant="small">{description}</Text>
        </>
      )}
    </>
  );
};

type DangerZoneTitleProps = {
  title: string;
  description: React.ReactNode;
};

export const DangerZoneTitle: React.FC<DangerZoneTitleProps> = ({ title, description }) => {
  return (
    <>
      <Text variant="body" color={invariantColor.white}>
        {t("common.danger_zone")}
      </Text>

      <Space height={12} />

      <Text variant="small" color={invariantColor.white}>
        {title}
      </Text>
      <Space height={4} />
      <Text variant="small">{description}</Text>
    </>
  );
};

type AppBackLinkProps = {
  to: string;
  label: string;
};

export const AppBackLink: React.FC<AppBackLinkProps> = ({ to, label }) => {
  return (
    <Link style={styles.backLink} to={to} variant="small">
      <Icon name="arrow_left" size={16} color={colors.neutral[200]} />
      <Space width={8} />
      {label}
    </Link>
  );
};
